import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { FunctionalTrainerBuilder } from "../../functionalTrainerBuilder"
import type { TwinContext } from "./twinParts"
import {
  createSideMeshes,
  createFrontBottomPulley,
  createHalfRackTopPulley,
  createBackBottomPulley,
  createTopRailPulley,
  createBottomRail,
  createBackPost,
  createBackShroud,
  createRearCrossmember,
  createTrolleyClip,
  createLatBarClip,
  createFootplate,
} from "./twinParts"

// TwinFMBuilder — Assembles FM-6-Twin and FM-HR-Twin onto a power rack
export class TwinFMBuilder extends FunctionalTrainerBuilder {
  
  protected getModelPrefix(): string {
    return 'FM_TWIN'
  }
  
  protected getSideSlotName(): string {
    return 'side_3' // Twin uses side_3 instead of side_2
  }

  // MARK: Context Factory
  private createContext(materials: MaterialInfo): TwinContext {
    return {
      config     : this.config,
      materials,
      height     : this.config.height,
      depth      : this.getDepthInInches(),
      isTwin80   : this.config.height === '80',
      isHalfRack : !this.config.sixPost,
    }
  }
  
  // MARK: J-Cup Offsets
  // 80" HR Twin: lower J-cups to clear the FT carriage geometry
  protected adjustJCupOffsets(baseRack: PartDefinition): void {
    if (!this.config.halfRack || this.config.height !== '80' || !this.config.jcup) return

    const jCupOffset = { y: -14 }

    const leftJcup = baseRack.slots['jcup-high']
    if (leftJcup && !Array.isArray(leftJcup)) {
      leftJcup.offset = jCupOffset
    }

    const rightUpright = baseRack.slots['other-side'] as PartDefinition
    if (rightUpright) {
      const rightJcup = rightUpright.slots['jcup-high']
      if (rightJcup && !Array.isArray(rightJcup)) {
        rightJcup.offset = jCupOffset
      }
    }
  }

  // MARK: Side Meshes
  protected buildSideMeshes(materials: MaterialInfo, left: boolean): PartDefinition[] {
    return createSideMeshes(this.createContext(materials), left)
  }
  
  // MARK: Front Bottom Pulley
  protected buildFrontBottomPulley(materials: MaterialInfo, left: boolean): PartDefinition {
    const context = this.createContext(materials)
    const trolleyClip = createTrolleyClip(left, this.config.trolleySelection)
    const carriage = this.buildSharedCarriage(trolleyClip, materials, left)
    
    return createFrontBottomPulley(context, left, trolleyClip, carriage)
  }

  // MARK: Resistance System
  protected addResistanceSystem(backUpright: PartDefinition, materials: MaterialInfo): void {
    const context = this.createContext(materials)
    const resistanceMeshes: PartDefinition[] = []
    
    const footplate = createFootplate(context)
    const latBarClip = createLatBarClip(this.materialBuilder.createLatBarMaterial(), this.config.latBarSelection)
    const backPost = createBackPost(context, latBarClip)
    
    resistanceMeshes.push(backPost)
    
    if (this.config.ftShrouds) {
      resistanceMeshes.push(createBackShroud(context))
    }
    
    const rearCrossmember = createRearCrossmember(context, footplate, resistanceMeshes)
    backUpright.slots['crossmember-bottom'] = rearCrossmember
  }
  
  // MARK: Half Rack Attachment
  protected attachToHalfRack(
    sideMeshes: PartDefinition[],
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    left: boolean
  ): void {
    const context = this.createContext(this.materialBuilder.createMainMaterial())
    
    // Half rack has the pulleys in the same slot as the stack
    sideMeshes.push(frontBottomPulley)
    
    const topPulley = createHalfRackTopPulley(context, left)
    
    topCrossmember.slots.side_2 = topPulley
    bottomCrossmember.slots[this.getSideSlotName()] = sideMeshes
  }
  
  // MARK: Full Rack Attachment
  protected attachToFullRack(
    sideMeshes: PartDefinition[],
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    materials: MaterialInfo,
    left: boolean
  ): void {
    const context = this.createContext(materials)
    const targetCrossmember = this.getBackBottomCrossmember(bottomCrossmember)
    
    // Add back bottom pulley to side meshes
    sideMeshes.push(createBackBottomPulley(context, left))
    
    // Attach rails system to front
    this.attachRailsSystem(context, frontBottomPulley, topCrossmember, left)
    
    // Attach side meshes to back crossmember
    targetCrossmember.slots[this.getSideSlotName()] = sideMeshes
    
    // Attach front bottom pulley to front crossmember
    bottomCrossmember.slots['4'] = [frontBottomPulley]
  }
  
  // MARK: Rails System Attachment
  private attachRailsSystem(
    context: TwinContext,
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    left: boolean
  ): void {
    topCrossmember.slots.ftx = createTopRailPulley(context, left)
    frontBottomPulley.slots.rail = createBottomRail(context, left)
  }
}